import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { load as loadTeam } from 'js/actions/team-actions';
import {
  deleteDraft,
  getDraft,
  getDraftAssessment,
  getTemplateState,
  mergeDraft,
  promoteDraft,
} from 'js/api/assessment-drafts';
import { AsyncDisplay, LoadingMessage } from 'js/components';
import { EditorInterface as SurveyEditor } from 'js/components/Assessment/Editor/Editor';
import { EditorInterface as LiveEditor } from 'js/components/Assessment/Editors/LiveAssessmentEditor/Editor';
import AsyncButton from 'js/components/Forms/AsyncButton';
import { getEngineStrategies } from 'js/engines';
import { useNotify } from 'js/hooks/useNotify';
import {
  fromBase64,
  revisionFor,
  snapshotOf,
  toBase64,
} from 'js/utils/assessment-draft';
import { rebaseDraft } from 'js/utils/draft-rebase';
import { assessmentPayloadFromTemplate } from 'js/utils/workspace-template';
import { DocumentProvider, useDoc, useProviders } from 'js/ydoc';

import LaunchDraftModal, {
  LAUNCH_CHOICES,
} from 'js/routes/Assessments/components/CustomizeAssessment/LaunchDraftModal';

import styles from './AssessmentEditorComponent.module.scss';

/**
 * Waits until the server has every edit made so far. The channel handles one
 * message at a time, so once it answers this, the edits sent before it are in.
 */
function flushEdits(channelProvider) {
  const channel = channelProvider?.channel;

  if (!channel) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    channel
      .push('force_persist', {})
      .receive('ok', resolve)
      .receive('error', reject)
      .receive('timeout', reject);
  });
}

function LaunchDraftAction({ draft, team, assessment, hasErrors }) {
  const ydoc = useDoc();
  const providers = useProviders();
  const dispatch = useDispatch();
  const history = useHistory();
  const notify = useNotify();
  const token = useSelector((state) => state.identity.token);
  const workspaceId = useSelector((state) => state.currentWorkspaceId);
  const [isOpen, setIsOpen] = useState(false);
  const [unpublishedChanges, setUnpublishedChanges] = useState(false);
  // A launch that updated or created a template, kept so a retry after a
  // failed assessment launch doesn't publish it again
  const settled = useRef(null);

  // Whether the template has edits in its editor nobody has published, so
  // updating it can say what happens to them
  useEffect(() => {
    if (!isOpen || !draft.source_template_id || !draft.source_baseline) {
      return;
    }

    getTemplateState(token, workspaceId, draft.source_template_id)
      .then((state) => setUnpublishedChanges(state.has_unpublished_changes))
      .catch(() => setUnpublishedChanges(false));
  }, [isOpen, draft, token, workspaceId]);

  // Settles the draft and returns the assessment to launch, in the shape a
  // published template has.
  const settle = useCallback(
    async (choice, newName) => {
      await flushEdits(providers.channelProvider);

      switch (choice) {
        case LAUNCH_CHOICES.UPDATE: {
          const state = await getTemplateState(
            token,
            workspaceId,
            draft.source_template_id
          );
          const { update, template } = rebaseDraft(
            fromBase64(state.update),
            ydoc,
            draft.source_baseline
          );
          const { revision } = await mergeDraft(token, workspaceId, draft.id, {
            update: toBase64(update),
            revision: revisionFor(assessment, {
              crdtSnapshot: snapshotOf(template),
            }),
          });

          return { launched: revision.assessment };
        }

        case LAUNCH_CHOICES.NEW: {
          const { revision } = await promoteDraft(
            token,
            workspaceId,
            draft.id,
            {
              template: {
                name: newName,
                description: assessment.meta.description,
              },
              revision: revisionFor(assessment, { doc: ydoc, name: newName }),
            }
          );

          // Only once it's saved: the document is the new template's now,
          // and carries its name for the editor
          ydoc.getMap('meta').set('name', newName);
          await flushEdits(providers.channelProvider).catch(() => {});

          return { launched: revision.assessment };
        }

        default:
          return {
            launched: await getDraftAssessment(token, workspaceId, draft.id),
          };
      }
    },
    [providers.channelProvider, ydoc, draft, token, workspaceId, assessment]
  );

  const handleLaunch = useCallback(
    async (choice, newName) => {
      try {
        // A template already updated or created by an earlier attempt isn't
        // touched again; that attempt's launch picks up where it failed
        const { launched, choice: settledAs } = settled.current ?? {
          ...(await settle(choice, newName)),
          choice,
        };

        if (settledAs !== LAUNCH_CHOICES.ONCE) {
          settled.current = { launched, choice: settledAs };
        }

        const payload = assessmentPayloadFromTemplate({
          type: draft.type,
          assessment: launched,
        });
        const { create, buildPostCreateRedirectPath } = getEngineStrategies({
          type: draft.type,
        });
        // The topics the question sets point at, as the draft defines them
        const created = await dispatch(
          create({ ...payload, config: assessment.config, team_id: team.id })
        );

        // Saving as a new template already turned the draft into it
        if (settledAs !== LAUNCH_CHOICES.NEW) {
          await deleteDraft(token, workspaceId, draft.id).catch(() => {});
        }

        providers.indexedDbProvider?.clearData();
        history.replace(buildPostCreateRedirectPath(created));
      } catch (error) {
        console.error('Failed to launch customized assessment', error);
        notify({
          type: 'danger',
          message: 'There was a problem launching the assessment.',
        });
      }
    },
    [
      settle,
      draft,
      team,
      assessment,
      dispatch,
      token,
      workspaceId,
      providers,
      history,
      notify,
    ]
  );

  return (
    <>
      <AsyncButton
        color="primary"
        onClick={() => setIsOpen(true)}
        disabled={hasErrors || !team}
      >
        Launch
      </AsyncButton>
      <LaunchDraftModal
        isOpen={isOpen}
        onCancel={() => setIsOpen(false)}
        onLaunch={handleLaunch}
        teamName={team?.name}
        templateName={draft.name}
        canUpdateTemplate={
          !!draft.source_template_id && !!draft.source_baseline
        }
        templateHasUnpublishedChanges={unpublishedChanges}
      />
    </>
  );
}

/**
 * The template editor, open on a customized-assessment draft instead of a
 * template. Edits sync live, but only into the draft; what becomes of them is
 * decided at launch. The draft knows its team and template, so the route only
 * needs its id.
 */
export function DraftEditorComponent() {
  const { workspaceId, draftId } = useParams();
  const token = useSelector((state) => state.identity.token);
  const dispatch = useDispatch();
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const teamId = draft?.team_id;
  const team = useSelector((state) => state.entities.teams?.[teamId]);

  useEffect(() => {
    if (teamId) {
      dispatch(loadTeam(teamId));
    }
  }, [dispatch, teamId]);

  useEffect(() => {
    getDraft(token, workspaceId, draftId)
      .then((loaded) => {
        setDraft(loaded);
        setError(null);
      })
      .catch(setError);
  }, [token, workspaceId, draftId]);

  // Back to the configure step this draft was started from
  const configurePath = `/w/${workspaceId}/teams/${teamId}/assessments/${draft?.source_key}/new`;
  const Editor = draft?.type === 'live' ? LiveEditor : SurveyEditor;

  const headerAction = useCallback(
    (props) => <LaunchDraftAction draft={draft} team={team} {...props} />,
    [draft, team]
  );

  return (
    <div className="clear-main-footer">
      <AsyncDisplay isLoading={!draft && !error} isError={!!error}>
        <AsyncDisplay.Loading>
          <LoadingMessage iconSize="2x">
            Loading the draft&hellip;
          </LoadingMessage>
        </AsyncDisplay.Loading>
        <AsyncDisplay.Error>
          <div className="danger">
            This draft is no longer available. It may have been launched
            already.
          </div>
        </AsyncDisplay.Error>
        <AsyncDisplay.Resolved>
          {draft && (
            <div className={styles.editor}>
              <DocumentProvider
                channelProviderToken={token}
                channelProviderTopic={`crdt:assessment-draft:${draft.crdt_doc_id}`}
              >
                <Editor backTo={configurePath} headerAction={headerAction} />
              </DocumentProvider>
            </div>
          )}
        </AsyncDisplay.Resolved>
      </AsyncDisplay>
    </div>
  );
}

export default DraftEditorComponent;
